import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DEFAULT_FILTERS } from '../../../shared/services/insightsApi';

const TIME_PERIODS = [
  { value: '3mo', label: '3 Months' },
  { value: '6mo', label: '6 Months' },
  { value: '1yr', label: '1 Year' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

function isNonDefault(filters) {
  return (
    filters.location !== DEFAULT_FILTERS.location ||
    filters.timePeriod !== DEFAULT_FILTERS.timePeriod ||
    (filters.jobTypes && filters.jobTypes.length > 0) ||
    (filters.categories && filters.categories.length > 0) ||
    filters.startDate ||
    filters.endDate
  );
}

/** Reusable multi-select dropdown used for both job types and categories */
function MultiSelectDropdown({
  id,
  label,
  allOptions,
  selectedOptions,
  onToggle,
  onSelectAll,
  onClear,
  activeColorClass,
  isLoading,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Build button label
  let buttonLabel = `All ${label}`;
  if (selectedOptions.length === 1) {
    buttonLabel = selectedOptions[0];
  } else if (selectedOptions.length === 2) {
    buttonLabel = selectedOptions.join(', ');
  } else if (selectedOptions.length > 2) {
    buttonLabel = `${selectedOptions.length} ${label}`;
  }

  const isActive = selectedOptions.length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        disabled={isLoading}
        className={`flex items-center gap-2 text-sm border rounded-lg px-3 py-1.5 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
          isActive
            ? activeColorClass
            : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="max-w-[140px] truncate">{buttonLabel}</span>
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg z-30 min-w-[200px] max-h-72 overflow-y-auto py-1">
          {/* Select all / clear header */}
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex justify-between sticky top-0 bg-white dark:bg-gray-900">
            <button
              onClick={() => onSelectAll(allOptions)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium"
            >
              Select all
            </button>
            <button
              onClick={onClear}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>

          {allOptions.map((option) => {
            const checked = selectedOptions.includes(option);
            return (
              <label
                key={option}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(option)}
                  className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-700 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">{option}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * InsightsFilterBar — sticky filter controls for the Insights page.
 *
 * Props:
 *   filters             - current filter state
 *   onChange            - (updatedFilters) => void
 *   availableJobTypes   - string[] of job types in the dataset
 *   availableLocations  - string[] of state abbreviations in the dataset
 *   availableCategories - string[] of category names in the dataset
 *   isLoading           - boolean, disables controls during fetch
 */
export function InsightsFilterBar({
  filters,
  onChange,
  availableJobTypes = [],
  availableLocations = [],
  availableCategories = [],
  isLoading = false,
}) {
  const handleLocationChange = useCallback(
    (e) => onChange({ ...filters, location: e.target.value }),
    [filters, onChange]
  );

  const handleTimePeriodChange = useCallback(
    (value) => {
      const update = { ...filters, timePeriod: value };
      if (value !== 'custom') {
        update.startDate = '';
        update.endDate = '';
      }
      onChange(update);
    },
    [filters, onChange]
  );

  const handleJobTypeToggle = useCallback(
    (type) => {
      const current = filters.jobTypes || [];
      const updated = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      onChange({ ...filters, jobTypes: updated });
    },
    [filters, onChange]
  );

  const handleCategoryToggle = useCallback(
    (cat) => {
      const current = filters.categories || [];
      const updated = current.includes(cat)
        ? current.filter((c) => c !== cat)
        : [...current, cat];
      onChange({ ...filters, categories: updated });
    },
    [filters, onChange]
  );

  const handleClearFilters = useCallback(
    () => onChange({ ...DEFAULT_FILTERS }),
    [onChange]
  );

  const hasActiveFilters = isNonDefault(filters);

  return (
    <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-3 py-3">

          {/* Geography filter */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
              Location
            </label>
            <select
              value={filters.location}
              onChange={handleLocationChange}
              disabled={isLoading}
              className="text-sm border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 cursor-pointer min-w-[120px]"
            >
              <option value="">All Locations</option>
              {availableLocations.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />

          {/* Time period segmented control */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:block">
              Period
            </label>
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden bg-gray-50 dark:bg-gray-900">
              {TIME_PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => handleTimePeriodChange(period.value)}
                  disabled={isLoading}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50 ${
                    filters.timePeriod === period.value
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date range — only shown when timePeriod === 'custom' */}
          {filters.timePeriod === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => onChange({ ...filters, startDate: e.target.value })}
                disabled={isLoading}
                className="text-sm border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
              <input
                type="date"
                value={filters.endDate}
                min={filters.startDate}
                onChange={(e) => onChange({ ...filters, endDate: e.target.value })}
                disabled={isLoading}
                className="text-sm border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          )}

          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />

          {/* Job type multi-select */}
          {availableJobTypes.length > 0 && (
            <MultiSelectDropdown
              id="jobTypes"
              label="Types"
              allOptions={availableJobTypes}
              selectedOptions={filters.jobTypes || []}
              onToggle={handleJobTypeToggle}
              onSelectAll={(all) => onChange({ ...filters, jobTypes: all })}
              onClear={() => onChange({ ...filters, jobTypes: [] })}
              activeColorClass="border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              isLoading={isLoading}
            />
          )}

          {/* Category multi-select */}
          {availableCategories.length > 0 && (
            <MultiSelectDropdown
              id="categories"
              label="Categories"
              allOptions={availableCategories}
              selectedOptions={filters.categories || []}
              onToggle={handleCategoryToggle}
              onSelectAll={(all) => onChange({ ...filters, categories: all })}
              onClear={() => onChange({ ...filters, categories: [] })}
              activeColorClass="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
              isLoading={isLoading}
            />
          )}

          {/* Clear all filters */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              disabled={isLoading}
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150 disabled:opacity-50 ml-auto"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear filters
            </button>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
              <span className="inline-block w-3 h-3 border-2 border-blue-300 dark:border-blue-700 border-t-blue-600 rounded-full animate-spin" />
              Loading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsightsFilterBar;
